import type { Expr, ExprCmp, ExprCondition, ExprOp } from '@kkp-fraud/shared';
import { FIELD_GROUPS, type FieldName } from '../fields';
import { CMP_SYMBOL, EXPR_CMPS, EXPR_OPS, MAX_EXPR_DEPTH, OP_SYMBOL, arity, operandKind, type OperandKind } from '../expressions';
import { t } from '../i18n';

/**
 * 2a expressions — the condition builder of an expression row: rows of
 * [AND/OR] [left operand] [comparison] [right operand | low … high] [✕]. An operand is a counter
 * of this row, a field of the current transaction, a number (text and true/false for = / ≠), or
 * a calculation (+ − × ÷ min max abs mod) of 1–4 operands, nested at most 4 deep. It checks
 * nothing itself (Wave 2-0): the engine's problems arrive through `notes`, by condition.
 */

export interface ExprConditionsProps {
  conditions: ExprCondition[];
  /** The row's counter names, for the "counter" operand. */
  names: readonly string[];
  onChange: (next: ExprCondition[]) => void;
  /** Action row number (1-based), for aria-labels and test ids. */
  n: number;
  readOnly?: boolean;
  /** Fields offered for "field of this transaction". Omitted = every field. */
  allowedFields?: readonly FieldName[];
  /** The engine's problems about each condition (anywhere inside it), by 0-based index. */
  notes?: ReadonlyMap<number, readonly string[]>;
}

const KINDS: readonly OperandKind[] = ['m', 'field', 'num', 'text', 'bool', 'calc'];

function emptyOf(kind: OperandKind, names: readonly string[], previous: Expr | undefined): Expr {
  switch (kind) {
    case 'm':
      return { m: names[0] ?? '' };
    case 'field':
      return { field: 'amount_thb' };
    case 'text':
      return '';
    case 'bool':
      return true;
    case 'calc':
      // the operand the analyst had becomes the first value of the calculation
      return { op: 'add', args: [previous ?? 0, 0] };
    default:
      return 0;
  }
}

interface OperandProps {
  value: Expr | undefined;
  onChange: (next: Expr) => void;
  names: readonly string[];
  /** Operators above this operand. */
  depth: number;
  /** Words for aria-labels: "Action 1, condition 2, left". */
  where: string;
  testId: string;
  readOnly: boolean;
  fields: readonly FieldName[];
}

function Operand({ value, onChange, names, depth, where, testId, readOnly, fields }: OperandProps) {
  const kind = operandKind(value);
  const kinds = depth >= MAX_EXPR_DEPTH ? KINDS.filter((k) => k !== 'calc') : KINDS;
  return (
    <span className="expr-operand pillwrap" data-testid={testId}>
      <select
        className="pill"
        aria-label={t('expr.operand.kind.a11y', { where })}
        disabled={readOnly}
        value={kind}
        onChange={(e) => onChange(emptyOf(e.target.value as OperandKind, names, value))}
      >
        {kinds.map((k) => (
          <option key={k} value={k}>
            {t(`expr.operand.kind.${k}`)}
          </option>
        ))}
      </select>
      {kind === 'm' && (
        <select
          className="pill field"
          aria-label={t('expr.operand.value.a11y', { where })}
          disabled={readOnly}
          value={value && typeof value === 'object' && 'm' in value ? value.m : ''}
          onChange={(e) => onChange({ m: e.target.value })}
        >
          {/* A name the row no longer has stays visible, so the engine's "unknown counter" makes sense. */}
          {value && typeof value === 'object' && 'm' in value && !names.includes(value.m) && <option value={value.m}>{value.m || '…'}</option>}
          {names.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
      )}
      {kind === 'field' && (
        <select
          className="pill field"
          aria-label={t('expr.operand.value.a11y', { where })}
          disabled={readOnly}
          value={value && typeof value === 'object' && 'field' in value ? String(value.field) : ''}
          onChange={(e) => onChange({ field: e.target.value as FieldName })}
        >
          {FIELD_GROUPS.map((g) => (
            <optgroup key={g.group} label={t(g.labelKey)}>
              {g.fields
                .filter((f) => fields.includes(f))
                .map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      )}
      {kind === 'num' && (
        <input
          type="number"
          className="f-val num-input"
          aria-label={t('expr.operand.value.a11y', { where })}
          disabled={readOnly}
          value={typeof value === 'number' && Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(e.target.value === '' ? Number.NaN : Number(e.target.value))}
        />
      )}
      {kind === 'text' && (
        <input
          type="text"
          className="f-val"
          aria-label={t('expr.operand.value.a11y', { where })}
          disabled={readOnly}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      {kind === 'bool' && (
        <select
          className="pill"
          aria-label={t('expr.operand.value.a11y', { where })}
          disabled={readOnly}
          value={String(value === true)}
          onChange={(e) => onChange(e.target.value === 'true')}
        >
          <option value="true">{t('expr.bool.true')}</option>
          <option value="false">{t('expr.bool.false')}</option>
        </select>
      )}
      {kind === 'calc' && value && typeof value === 'object' && 'op' in value && (
        <Calculation value={value} onChange={onChange} names={names} depth={depth} where={where} testId={testId} readOnly={readOnly} fields={fields} />
      )}
    </span>
  );
}

function Calculation({ value, onChange, names, depth, where, testId, readOnly, fields }: OperandProps & { value: { op: ExprOp; args: Expr[] } }) {
  const [, max] = arity(value.op);
  const [min] = arity(value.op);
  const setOp = (op: ExprOp) => {
    const [lo, hi] = arity(op);
    let args = value.args.slice(0, hi);
    while (args.length < lo) args = [...args, 0];
    onChange({ op, args });
  };
  const setArg = (i: number, next: Expr) => onChange({ op: value.op, args: value.args.map((a, x) => (x === i ? next : a)) });
  return (
    <span className="expr-calc">
      (
      {value.args.map((arg, i) => (
        <span key={i} className="expr-arg">
          {i === 1 && (
            <select
              className="pill op"
              aria-label={t('expr.operand.op.a11y', { where })}
              disabled={readOnly}
              value={value.op}
              onChange={(e) => setOp(e.target.value as ExprOp)}
            >
              {EXPR_OPS.map((op) => (
                <option key={op} value={op}>
                  {OP_SYMBOL[op]}
                </option>
              ))}
            </select>
          )}
          {i > 1 && <span className="pill op">{OP_SYMBOL[value.op]}</span>}
          <Operand
            value={arg}
            onChange={(next) => setArg(i, next)}
            names={names}
            depth={depth + 1}
            where={`${where} · ${i + 1}`}
            testId={`${testId}-${i + 1}`}
            readOnly={readOnly}
            fields={fields}
          />
          {!readOnly && value.args.length > min && (
            <button
              type="button"
              className="btn small ghost rowdel"
              aria-label={t('expr.operand.arg.remove.a11y', { where, i: i + 1 })}
              onClick={() => onChange({ op: value.op, args: value.args.filter((_, x) => x !== i) })}
            >
              ✕
            </button>
          )}
        </span>
      ))}
      {/* A one-operand operator (abs) chooses its operator here, as there is no second operand to put it before. */}
      {value.args.length === 1 && (
        <select
          className="pill op"
          aria-label={t('expr.operand.op.a11y', { where })}
          disabled={readOnly}
          value={value.op}
          onChange={(e) => setOp(e.target.value as ExprOp)}
        >
          {EXPR_OPS.map((op) => (
            <option key={op} value={op}>
              {OP_SYMBOL[op]}
            </option>
          ))}
        </select>
      )}
      {!readOnly && value.args.length < max && (
        <button type="button" className="pill add" onClick={() => onChange({ op: value.op, args: [...value.args, 0] })}>
          {t('expr.operand.arg.add')}
        </button>
      )}
      )
    </span>
  );
}

export function ExprConditions({ conditions, names, onChange, n, readOnly = false, allowedFields, notes }: ExprConditionsProps) {
  const fields = allowedFields ?? FIELD_GROUPS.flatMap((g) => g.fields);
  const set = (i: number, change: Partial<ExprCondition>) => onChange(conditions.map((c, x) => (x === i ? ({ ...c, ...change } as ExprCondition) : c)));
  const setCmp = (i: number, cmp: ExprCmp) => {
    const c = conditions[i]!;
    const one = Array.isArray(c.right) ? c.right[0] : c.right;
    const right: ExprCondition['right'] = cmp === 'between' ? (Array.isArray(c.right) ? c.right : [one, one]) : one;
    set(i, { cmp, right });
  };
  const add = () => {
    const c: ExprCondition = { left: { m: names[0] ?? '' }, cmp: 'gte', right: 1 };
    if (conditions.length > 0) c.logic = 'AND';
    onChange([...conditions, c]);
  };
  return (
    <div className="expr-conditions stack" data-testid={`pb-b${n}-conditions`}>
      <div className="small muted">{t('expr.conditions.label')}</div>
      {conditions.map((c, i) => {
        const where = (side: string) => t(`expr.where.${side}`, { n, c: i + 1 });
        const id = `pb-b${n}-c${i + 1}`;
        const list = notes?.get(i) ?? [];
        return (
          <div key={i} className={`expr-condition row-wrap${list.length > 0 ? ' invalid' : ''}`} data-testid={id}>
            {i > 0 && (
              <select
                className="pill logic"
                aria-label={t('expr.condition.join.a11y', { n, c: i + 1 })}
                disabled={readOnly}
                value={c.logic ?? 'AND'}
                onChange={(e) => set(i, { logic: e.target.value as 'AND' | 'OR' })}
              >
                <option value="AND">AND</option>
                <option value="OR">OR</option>
              </select>
            )}
            <Operand value={c.left} onChange={(left) => set(i, { left })} names={names} depth={0} where={where('left')} testId={`${id}-left`} readOnly={readOnly} fields={fields} />
            <select
              className="pill op"
              aria-label={t('expr.condition.cmp.a11y', { n, c: i + 1 })}
              disabled={readOnly}
              value={c.cmp}
              onChange={(e) => setCmp(i, e.target.value as ExprCmp)}
            >
              {EXPR_CMPS.map((cmp) => (
                <option key={cmp} value={cmp}>
                  {cmp === 'between' ? t('expr.cmp.between.option') : CMP_SYMBOL[cmp]}
                </option>
              ))}
            </select>
            {c.cmp === 'between' && Array.isArray(c.right) ? (
              <>
                <Operand value={c.right[0]} onChange={(lo) => set(i, { right: [lo, (c.right as [Expr, Expr])[1]] })} names={names} depth={0} where={where('low')} testId={`${id}-low`} readOnly={readOnly} fields={fields} />
                <span className="small muted">{t('expr.between.and')}</span>
                <Operand value={c.right[1]} onChange={(hi) => set(i, { right: [(c.right as [Expr, Expr])[0], hi] })} names={names} depth={0} where={where('high')} testId={`${id}-high`} readOnly={readOnly} fields={fields} />
              </>
            ) : (
              <Operand
                value={Array.isArray(c.right) ? c.right[0] : c.right}
                onChange={(right) => set(i, { right })}
                names={names}
                depth={0}
                where={where('right')}
                testId={`${id}-right`}
                readOnly={readOnly}
                fields={fields}
              />
            )}
            {!readOnly && conditions.length > 1 && (
              <button
                type="button"
                className="btn small ghost rowdel"
                aria-label={t('expr.condition.remove.a11y', { n, c: i + 1 })}
                onClick={() => {
                  const next = conditions.filter((_, x) => x !== i);
                  // the new first condition has no join
                  if (next[0]?.logic !== undefined) {
                    const { logic: _drop, ...first } = next[0];
                    next[0] = first;
                  }
                  onChange(next);
                }}
              >
                ✕
              </button>
            )}
            {list.length > 0 && (
              <div className="why-missing" role="alert" data-testid={`${id}-problem`} style={{ flexBasis: '100%' }}>
                <ul>
                  {list.map((text, x) => (
                    <li key={x}>{text}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button type="button" className="pill add" onClick={add}>
          {t('expr.condition.add')}
        </button>
      )}
    </div>
  );
}
